import axios from 'axios';
import { appConfig } from '../config/appConfig';
import { auth } from './auth';
import { shouldSkipAuthRefresh } from '../utils/authErrorMessage';

export const DEFAULT_PROFILE_IMAGE = '/default-avatar.png';

export const api = axios.create({
  baseURL: appConfig.api.mainURL,
  withCredentials: true
});

// Request queue for handling token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add an interceptor to handle credentials properly
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor
api.interceptors.request.use((config) => {
  // Add tenantId to headers if it exists and it's not an auth request
  const tenantId = localStorage.getItem('tenantId');
  if (tenantId && !config.url.includes('/auth/')) {
    config.headers['tenantId'] = tenantId;
  }

  // Add timezone header using browser's timezone
  config.headers['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config || {};
    const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
    const isRefreshRequest = String(originalRequest.url || '').includes('/auth/refresh');
    const skipRefresh = shouldSkipAuthRefresh(originalRequest) || isLoginPage;

    // Don't retry if it's already been retried or is a refresh token request
    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest && !skipRefresh) {
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch(err => {
            // Don't redirect on login page failures
            if (!isLoginPage) {
              handleAuthError(err);
            }
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('/v1/auth/refresh');
        const { accessToken } = response.data;
        
        if (accessToken) {
          localStorage.setItem('token', accessToken);
        }
        
        processQueue(null, accessToken);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Only clear auth state and redirect if not on login page
        if (!isLoginPage) {
          handleAuthError(refreshError);
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    //return Promise.reject(handleApiError(error));
    return Promise.reject(error);
  }
);

// Helper function to handle auth errors
const handleAuthError = (error) => {
  localStorage.clear();
  window.location.href = '/login';
  return error;
};

export const uploadProfileImage = async (imageFile) => {
  if (!imageFile || !(imageFile instanceof File)) {
    throw new Error('Invalid image file');
  }

  if (imageFile.size > 5 * 1024 * 1024) {
    throw new Error('File size exceeds 5MB limit');
  }

  const formData = new FormData();
  formData.append('image', imageFile);

  try {
    const response = await api.post('/application-users/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    updateProfileImageTimestamp(response.data?.userId);
    return response.data;
  } catch (error) {
    console.error('Error uploading profile image:', error);
    throw new Error(error.response?.data?.error || 'Failed to upload profile image');
  }
};

export const getProfileImageUrl = (userId) => {
  if (!userId) return DEFAULT_PROFILE_IMAGE;
  const timestamp = localStorage.getItem(`profileImageTimestamp_${userId}`) || Date.now();
  return `${api.defaults.baseURL}/application-users/profile-image/${userId}?v=${timestamp}`;
};

export const updateProfileImageTimestamp = (userId) => {
  if (userId) {
    localStorage.setItem(`profileImageTimestamp_${userId}`, Date.now());
  }
};

export default api;
